/**
 * A custom MCP server's own page: who it is for, whether it is on, how it
 * connects (never the secret itself), and which of its tools Eve may use.
 *
 * One page for both scopes. An organization server is an admin's to change
 * and opens under Apps; a personal server is its
 * owner's alone and opens under Plugins. Every edit control follows the
 * server's own `canManage`, which the API computes for the caller.
 *
 * Tool choices are saved explicitly, like the scope editor's: a half-ticked
 * list must not widen what the organization's agents reach until the admin
 * says so. Write tools start unticked, and each call of one asks the person
 * in the chat for approval even when it is allowed here. Members see all of
 * it read-only.
 *
 * @module components/apps/integrations/CustomMcpServerPage
 */
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronRightIcon,
  Loader2Icon,
  PencilIcon,
  RefreshCwIcon,
  Trash2Icon,
  WebhookIcon,
} from "lucide-react";
import { useCallback, useId, useMemo, useState } from "react";

import { cn } from "~/lib/utils";
import { useOrgProfile } from "../../../state/orgProfile";
import {
  useCustomMcpServers,
  type CustomMcpResult,
  type CustomMcpServer,
  type CustomMcpTool,
} from "../../../state/customMcpServers";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Switch } from "../../ui/switch";
import { ListSkeleton } from "../../regulation/LoadingSkeletons";
import { CustomMcpServerDialog } from "./CustomMcpServerDialog";
import {
  allowedToolsChanged,
  allowedToolsLabel,
  connectionFromForm,
  customMcpAuthLabel,
  customMcpHost,
  customMcpScopeLabel,
  toggleAllowedTool,
  type CustomMcpForm,
} from "./customMcpServerContent";

const formatDateTime = (value: string | null): string => {
  if (value === null) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

function SectionHeading({ title, count }: { readonly title: string; readonly count?: number }) {
  return (
    <h2 className="text-base font-semibold tracking-tight text-foreground">
      {title}
      {count !== undefined ? (
        <span className="ml-2 text-sm font-normal tabular-nums text-muted-foreground">{count}</span>
      ) : null}
    </h2>
  );
}

/**
 * How the server connects. The secret is only ever "stored" or not — never its
 * value. Who can use it is shown to org admins only: a member can only ever
 * hold personal servers, so for them the row says nothing.
 */
export function CustomMcpConnectionSummary({
  server,
  showScope = true,
}: {
  readonly server: CustomMcpServer;
  readonly showScope?: boolean;
}) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-2 text-sm">
      {showScope ? (
        <>
          <dt className="text-muted-foreground">Who can use it</dt>
          <dd className="text-foreground">{customMcpScopeLabel(server.scope)}</dd>
        </>
      ) : null}
      <dt className="text-muted-foreground">Server URL</dt>
      <dd className="min-w-0 break-all font-mono text-xs leading-5 text-foreground">
        {server.url}
      </dd>
      <dt className="text-muted-foreground">Authentication</dt>
      <dd className="text-foreground">{customMcpAuthLabel(server.authType)}</dd>
      {server.authType === "header" ? (
        <>
          <dt className="text-muted-foreground">Header name</dt>
          <dd className="font-mono text-xs leading-5 text-foreground">
            {server.headerName ?? "—"}
          </dd>
        </>
      ) : null}
      {server.authType !== "none" ? (
        <>
          <dt className="text-muted-foreground">Secret</dt>
          <dd className="text-foreground">
            {server.hasSecret ? "Stored encrypted" : "Not stored — edit the connection to add it"}
          </dd>
        </>
      ) : null}
      <dt className="text-muted-foreground">Tools last read</dt>
      <dd className="text-foreground">{formatDateTime(server.lastCheckedAt)}</dd>
      <dt className="text-muted-foreground">Added</dt>
      <dd className="text-foreground">{formatDateTime(server.createdAt)}</dd>
    </dl>
  );
}

/** The server's tools, each with a box that allows it for Eve. */
export function CustomMcpToolList({
  tools,
  allowed,
  disabled,
  canManage,
  onToggle,
}: {
  readonly tools: ReadonlyArray<CustomMcpTool>;
  readonly allowed: ReadonlyArray<string>;
  readonly disabled: boolean;
  readonly canManage: boolean;
  readonly onToggle: (name: string, next: boolean) => void;
}) {
  const listId = useId();
  return (
    <ul className="space-y-1.5" data-testid="custom-mcp-tools">
      {tools.map((tool) => (
        <li key={tool.name}>
          <label
            htmlFor={`${listId}-${tool.name}`}
            className={cn(
              "flex items-start gap-3 rounded-lg border border-border/60 bg-background px-3 py-2",
              canManage && "cursor-pointer",
            )}
          >
            <Checkbox
              id={`${listId}-${tool.name}`}
              checked={allowed.includes(tool.name)}
              disabled={disabled || !canManage}
              onCheckedChange={(next) => onToggle(tool.name, next === true)}
              aria-label={tool.name}
            />
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs font-medium text-foreground">{tool.name}</span>
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                    tool.effect === "read"
                      ? "bg-success/10 text-success"
                      : "bg-warning/10 text-warning",
                  )}
                  title={
                    tool.effect === "read"
                      ? "The server marks this tool read-only"
                      : "The server does not mark this tool read-only; treated as making changes"
                  }
                >
                  {tool.effect === "read" ? "read-only" : "may make changes"}
                </span>
              </span>
              {tool.description ? (
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {tool.description}
                </span>
              ) : null}
              {tool.effect === "write" ? (
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  Each call asks the person in the chat for approval first.
                </span>
              ) : null}
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
}

/** Where the page was opened from: its back link returns there. */
export type CustomMcpServerPageSection = "integrations" | "plugins";

const BACK_LINK_CLASS =
  "cursor-pointer text-muted-foreground transition-colors hover:text-foreground";

/** The back link for a section: Plugins for personal servers, Apps for the organization's. */
export const customMcpBackLink = (section: CustomMcpServerPageSection) =>
  section === "plugins"
    ? ({ to: "/$org/connections", label: "Plugins" } as const)
    : ({ to: "/$org/apps", label: "Apps" } as const);


/** The page's words for who a switched-on (or off) server serves. */
export const customMcpAvailability = (server: Pick<CustomMcpServer, "scope" | "enabled">) =>
  server.scope === "personal"
    ? {
        status: server.enabled ? "On for you" : "Off",
        detail: server.enabled
          ? "Eve can use the allowed tools below in chats you are driving. Nobody else sees this server."
          : "Off: Eve cannot reach this server until you turn it on.",
      }
    : {
        status: server.enabled ? "On for this organization" : "Off",
        detail: server.enabled
          ? "Eve can use the allowed tools below for everyone in the organization."
          : "Off: Eve cannot reach this server until an admin turns it on.",
      };

interface CustomMcpServerPageProps {
  readonly organizationId: string | null;
  readonly orgSegment: string;
  readonly serverId: string;
  readonly section?: CustomMcpServerPageSection;
}

export function CustomMcpServerPage({
  organizationId,
  orgSegment,
  serverId,
  section = "integrations",
}: CustomMcpServerPageProps) {
  const navigate = useNavigate();
  const {
    servers,
    isPending,
    error: loadError,
    update,
    refresh,
    remove,
  } = useCustomMcpServers(organizationId);
  const server = servers?.find((candidate) => candidate.id === serverId) ?? null;
  const canManage = server?.canManage ?? false;
  const { canManage: isOrgAdmin } = useOrgProfile(organizationId);
  const back = customMcpBackLink(section);

  const [busy, setBusy] = useState<"enabled" | "tools" | "refresh" | "remove" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  // The admin's unsaved tool choice; null while it matches what is stored.
  const [draft, setDraft] = useState<ReadonlyArray<string> | null>(null);

  const storedAllowed = useMemo(
    () => (server?.tools ?? []).filter((tool) => tool.allowed).map((tool) => tool.name),
    [server],
  );
  const allowed = draft ?? storedAllowed;
  const dirty = draft !== null && allowedToolsChanged(storedAllowed, draft);

  const run = useCallback(
    async (kind: NonNullable<typeof busy>, action: () => Promise<CustomMcpResult>) => {
      setBusy(kind);
      setError(null);
      const outcome = await action();
      setBusy(null);
      if (!outcome.ok) setError(outcome.message);
      return outcome;
    },
    [],
  );

  const saveConnection = useCallback(
    (form: CustomMcpForm) =>
      update(serverId, {
        ...(server !== null && form.name.trim() !== server.name ? { name: form.name } : {}),
        connection: connectionFromForm(form),
      }).then((outcome) => {
        // The catalogue was re-read; choices are judged against the new one.
        if (outcome.ok) setDraft(null);
        return outcome;
      }),
    [server, serverId, update],
  );

  if (server === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        {/* Not "not found" while the list is still being (re)read — right
            after a create the page opens before the refreshed list lands —
            or while the organization itself is resolving. */}
        {(organizationId === null || isPending) && loadError === null ? (
          <div className="w-full max-w-md">
            <ListSkeleton rows={4} />
          </div>
        ) : (
          <>
            <WebhookIcon className="size-6 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">
              {loadError ?? "Custom MCP server not found"}
            </p>
            <Button
              variant="outline"
              className="cursor-pointer rounded-full"
              // One literal route per section, so each link is typed against its own.
              render={
                section === "plugins" ? (
                  <Link to="/$org/connections" params={{ org: orgSegment }} />
                ) : (
                  <Link to="/$org/apps" params={{ org: orgSegment }} />
                )
              }
            >
              Back to {back.label.toLowerCase()}
            </Button>
          </>
        )}
      </div>
    );
  }

  const host = customMcpHost(server.url);
  const availability = customMcpAvailability(server);

  return (
    <div className="regulation-page-scroll-fade min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
        <nav className="flex items-center gap-1.5 text-sm">
          {section === "plugins" ? (
            <Link to="/$org/connections" params={{ org: orgSegment }} className={BACK_LINK_CLASS}>
              {back.label}
            </Link>
          ) : (
            <Link
              to="/$org/apps"
              params={{ org: orgSegment }}
              className={BACK_LINK_CLASS}
            >
              {back.label}
            </Link>
          )}
          <ChevronRightIcon className="size-3.5 text-muted-foreground/60" />
          <span className="font-medium text-foreground">{server.name}</span>
        </nav>

        <span className="mt-10 flex size-20 items-center justify-center rounded-3xl border border-border/60 bg-background">
          <WebhookIcon className="size-1/2 text-muted-foreground" />
        </span>

        <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">{server.name}</h1>
            <p className="mt-1.5 text-muted-foreground">
              Custom MCP server at <span className="font-mono text-sm">{host}</span>
            </p>
          </div>
          {canManage ? (
            <label className="flex shrink-0 cursor-pointer items-center gap-2.5 rounded-full border border-border/60 bg-card py-1.5 pl-3.5 pr-2">
              <span className="text-xs font-medium text-foreground">{availability.status}</span>
              <Switch
                checked={server.enabled}
                disabled={busy !== null}
                onCheckedChange={(next) =>
                  void run("enabled", () => update(server.id, { enabled: next }))
                }
                aria-label={`${server.enabled ? "Turn off" : "Turn on"} ${server.name}`}
              />
            </label>
          ) : (
            <span
              className={cn(
                "shrink-0 rounded-full px-3 py-1.5 text-xs font-medium",
                server.enabled ? "bg-success/10 text-success" : "bg-muted text-muted-foreground",
              )}
            >
              {availability.status}
            </span>
          )}
        </div>
        <p className="mt-4 max-w-2xl text-sm text-muted-foreground">{availability.detail}</p>

        {error !== null ? (
          <p role="alert" className="mt-4 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <section className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SectionHeading title="Connection" />
            {canManage ? (
              <Button
                size="sm"
                variant="outline"
                className="cursor-pointer rounded-full"
                disabled={busy !== null}
                onClick={() => setEditing(true)}
              >
                <PencilIcon className="size-3.5" />
                Edit connection
              </Button>
            ) : null}
          </div>
          <div className="mt-3 rounded-2xl border border-border/60 bg-card p-5">
            <CustomMcpConnectionSummary server={server} showScope={isOrgAdmin} />
          </div>
        </section>

        <section className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SectionHeading title="Tools" count={server.tools.length} />
            {canManage ? (
              <Button
                size="sm"
                variant="outline"
                className="cursor-pointer rounded-full"
                disabled={busy !== null}
                onClick={() =>
                  void run("refresh", () => refresh(server.id)).then((outcome) => {
                    if (outcome.ok) setDraft(null);
                  })
                }
              >
                <RefreshCwIcon className={cn("size-3.5", busy === "refresh" && "animate-spin")} />
                Refresh tools
              </Button>
            ) : null}
          </div>
          <div className="mt-3 space-y-4 rounded-2xl border border-border/60 bg-card p-5">
            {server.tools.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                The server listed no tools when it was last read.
                {canManage ? " Refresh once it offers some." : ""}
              </p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {allowedToolsLabel(
                    server.tools.map((tool) => ({ allowed: allowed.includes(tool.name) })),
                  )}
                  . Eve can only use the ticked ones; tools that may make changes start off.
                </p>
                <CustomMcpToolList
                  tools={server.tools}
                  allowed={allowed}
                  disabled={busy !== null}
                  canManage={canManage}
                  onToggle={(name, next) => setDraft(toggleAllowedTool(allowed, name, next))}
                />
                {canManage ? (
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      className="cursor-pointer rounded-full"
                      disabled={busy !== null || !dirty}
                      onClick={() => setDraft(null)}
                    >
                      Reset
                    </Button>
                    <Button
                      type="button"
                      className="cursor-pointer rounded-full"
                      disabled={busy !== null || !dirty}
                      onClick={() =>
                        void run("tools", () =>
                          update(server.id, { allowedTools: draft ?? storedAllowed }),
                        ).then((outcome) => {
                          if (outcome.ok) setDraft(null);
                        })
                      }
                    >
                      {busy === "tools" ? <Loader2Icon className="size-4 animate-spin" /> : null}
                      Save tools
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </section>

        {canManage ? (
          <section className="mt-10">
            <SectionHeading title="Remove" />
            <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card px-5 py-4">
              <p className="text-sm text-muted-foreground">
                {server.scope === "personal"
                  ? "Disconnects the server and forgets its stored secret."
                  : "Disconnects the server for everyone and forgets its stored secret."}
              </p>
              <Button
                size="sm"
                variant="destructive-outline"
                className="shrink-0 cursor-pointer rounded-full"
                disabled={busy !== null}
                onClick={() => setConfirmRemove(true)}
              >
                <Trash2Icon className="size-3.5" />
                Remove
              </Button>
            </div>
          </section>
        ) : null}
      </div>

      {canManage ? (
        <CustomMcpServerDialog
          open={editing}
          onOpenChange={setEditing}
          server={server}
          onSubmit={saveConnection}
        />
      ) : null}

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {server.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {server.scope === "personal"
                ? "Eve stops using its tools in your chats, and the stored secret is deleted."
                : "Eve stops using its tools for everyone in the organization, and the stored secret is deleted."}{" "}
              You can connect it again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>Cancel</AlertDialogClose>
            <Button
              variant="destructive"
              disabled={busy !== null}
              onClick={() =>
                void run("remove", () => remove(server.id)).then((outcome) => {
                  setConfirmRemove(false);
                  if (outcome.ok) {
                    if (section === "plugins") {
                      void navigate({ to: "/$org/connections", params: { org: orgSegment } });
                    } else {
                      void navigate({
                        to: "/$org/apps",
                        params: { org: orgSegment },
                      });
                    }
                  }
                })
              }
            >
              {busy === "remove" ? <Loader2Icon className="size-4 animate-spin" /> : null}
              Remove
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </div>
  );
}
